export type Tema = "claro" | "oscuro"

export const CLAVE_TEMA = "aduanal:tema"

/**
 * Los tokens de §7. **`estilos/globales.css` es el original y esto es la copia**
 * que la prueba de contraste puede leer.
 *
 * ⚠ **Que sean dos copias ya falló una vez.** Al pasar el acento de verde azulado
 * a vino borgoña, el CSS cambió y esto no: la suite siguió en verde midiendo el
 * contraste de un color que la interfaz ya no usaba, que es justo el silencio que
 * esta prueba existía para romper. Ahora `tema.test.ts` lee el CSS y compara, así
 * que la copia no puede volver a quedarse atrás sin que algo falle.
 */
export const PALETA = {
  claro: {
    primary: "#7a1b38",
    primaryFg: "#ffffff",
    primaryTexto: "#7a1b38",
    background: "#f8fafc",
    surface: "#ffffff",
    surfaceElevated: "#f1f5f9",
    border: "#e2e8f0",
    fg: "#0f172a",
    fgMuted: "#64748b",
  },
  oscuro: {
    primary: "#b34862",
    primaryFg: "#ffffff",
    primaryTexto: "#d597a2",
    background: "#0b0e14",
    surface: "#141922",
    surfaceElevated: "#1c2230",
    border: "#252c3b",
    fg: "#e6eaf2",
    fgMuted: "#94a0b8",
  },
} as const

/** Luminancia relativa de un color, según la fórmula de WCAG 2.2. */
export function luminancia(hex: string): number {
  const limpio = hex.replace("#", "")
  const canales = [0, 2, 4].map((i) => Number.parseInt(limpio.slice(i, i + 2), 16) / 255)
  const lineal = canales.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
  // Se desestructura con valor por defecto en vez de indexar: `tsconfig.json`
  // activa `noUncheckedIndexedAccess`, así que `lineal[0]` es `number | undefined`
  // para el compilador aunque aquí siempre haya tres canales.
  const [r = 0, g = 0, b = 0] = lineal
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Ratio de contraste entre dos colores.
 *
 * No es adorno: WCAG AA exige 4,5:1 para texto de cuerpo, y un par que reprueba
 * deja ilegible una tabla que esta gente mira ocho horas al día. Se comprueba en
 * la suite para que un cambio de paleta no lo rompa en silencio.
 */
export function contraste(a: string, b: string): number {
  const la = luminancia(a)
  const lb = luminancia(b)
  const claro = Math.max(la, lb)
  const oscuro = Math.min(la, lb)
  return (claro + 0.05) / (oscuro + 0.05)
}

export function leerTema(): Tema {
  if (typeof document === "undefined") return "oscuro"
  return document.documentElement.dataset.tema === "claro" ? "claro" : "oscuro"
}

export function aplicarTema(tema: Tema): void {
  if (typeof document === "undefined") return
  document.documentElement.dataset.tema = tema
  try {
    localStorage.setItem(CLAVE_TEMA, tema)
  } catch {
    // Modo privado o almacenamiento lleno: el tema no persiste, pero la
    // aplicación sigue funcionando. No es motivo para romper nada.
  }
}

// --- La apariencia que elige cada quien -----------------------------------------

/** Cuánto se oscurecen las fotografías de fondo, guardado por navegador. */
export const CLAVE_VELO = "aduanal:velo-fotos"

/**
 * El velo por omisión: el 30 % que se ajustó viéndolo en producción (ver
 * `--fondo-sombra`). Con él, quien nunca toque el control ve exactamente lo de antes.
 */
export const VELO_POR_OMISION = 0.3

/**
 * ⚠ **Un techo por debajo de 1, y no es timidez.** Con la sombra al 100 % las fotos
 * desaparecen, y eso ya es el modo claro con otro color: quien quiere el fondo liso
 * tiene esa opción aparte. El suelo en 0 sí vale — la foto entera, sin velo.
 */
export const VELO_MAXIMO = 0.85

/** Lo guardado, o lo de siempre si no hay nada o el navegador no deja leer. */
export function leerPreferencias(): { tema: Tema; velo: number } {
  try {
    const tema = localStorage.getItem(CLAVE_TEMA) === "claro" ? "claro" : "oscuro"
    const crudo = Number.parseFloat(localStorage.getItem(CLAVE_VELO) ?? "")
    const velo = Number.isFinite(crudo)
      ? Math.min(Math.max(crudo, 0), VELO_MAXIMO)
      : VELO_POR_OMISION
    return { tema, velo }
  } catch {
    // Modo privado: se ve lo de siempre, que es lo correcto sin preferencia.
    return { tema: "oscuro", velo: VELO_POR_OMISION }
  }
}

export function guardarPreferencias(tema: Tema, velo: number): void {
  try {
    localStorage.setItem(CLAVE_TEMA, tema)
    localStorage.setItem(CLAVE_VELO, String(velo))
  } catch {
    // Igual que `aplicarTema`: sin persistencia, pero sin romper nada.
  }
}
